import { Fragment, useRef } from "react";
import { connect } from "react-redux";
import { OPEN_RATING_MODAL } from "redux/chat";
import { apis } from "services/config";
import {
  copyToClipboard,
  formatTranscript,
  formatTranscriptHtml,
} from "utils/chatTranscript";
import trackEvent from "utils/trackEvent";
import { downloadFile } from "utils/utils";
import ChatFilterPanel from "components/ChatFilterPanel";
import GenAiSearchBar from "components/GenAiSearchBar";
import RatingCommentModal from "components/RatingCommentModal";
import StarRating from "components/StarRating";
import CopyIcon from "images/copy.svg";
import DownloadIcon from "images/download.svg";
import ChatResultActions from "./ChatResultActions";
import MessageList from "./MessageList";
import ResetSessionButton from "./ResetSessionButton";
import TileViewButton from "./TileViewButton";
import "./style.scss";

// Bookmarking is main-backend-owned (see ChatResultActions.js), so
// "Download All" here downloads the user's whole persisted bookmark
// collection through the same endpoint the existing Bookmarks page uses -
// not a chatbot-backend-owned export.
const handleDownloadBookmarks = async ({
  offeringTopicsCodes,
  qualificationsCodes,
}) => {
  trackEvent("CHAT_DOWNLOAD_BOOKMARKS");
  let queryParams = "?";
  offeringTopicsCodes.forEach(offering => {
    queryParams = `${queryParams}offeringTopicList[]=${offering}&`;
  });
  qualificationsCodes.forEach(qual => {
    queryParams = `${queryParams}qualificationList[]=${qual}&`;
  });
  await downloadFile(
    `${apis.BOOKMARK}/download/${queryParams}`,
    `Myracle-${+new Date()}.zip`
  );
};

// Always renders as a single chat window shell (header + message thread +
// input row), not a search box that separately grows a results panel
// underneath it once a conversation starts. GenAiSearchBar/ChatFilterPanel
// used to be siblings mounted directly on the Home page; they're rendered
// here instead so the whole thing reads as one persistent chat dialog.
const ChatPanel = ({ messages, status, bookmarks, openRatingModal }) => {
  const hasMessages = messages.length > 0;
  // The loading bubble lives inside MessageList, so the very first message
  // of a new session (messages still empty while its response is pending)
  // needs the thread shown too, not just the empty-state hint - otherwise
  // there's no "Myracle Bot: ..." feedback at all for that first turn.
  const showThread = hasMessages || status === "loading";
  const bodyRef = useRef(null);

  const renderMessageExtras = message => {
    if (message.role !== "assistant") return null;
    return (
      <Fragment>
        {message.entryId && (
          <StarRating
            rating={message.rating?.rating || 0}
            onRate={star =>
              openRatingModal({ entryId: message.entryId, rating: star })
            }
          />
        )}
        {/* Both render their own independent (redux-connected) buttons,
            but visually they're one row of result-action pills, not two
            stacked rows - see chat-result-actions-row in style.scss. */}
        <div className="chat-result-actions-row">
          <ChatResultActions message={message} />
          <TileViewButton message={message} />
        </div>
      </Fragment>
    );
  };

  return (
    <div className="chat-panel-container">
      <div className="chat-panel-header">
        <div className="chat-panel-title">
          <span className="chat-panel-title-dot" />
          Generative AI search
        </div>
        <div className="chat-panel-header-actions">
          {hasMessages && (
            <Fragment>
              <button
                type="button"
                className="chat-icon-btn"
                title="Copy entire chat transcript"
                aria-label="Copy entire chat transcript"
                onClick={() => {
                  trackEvent("CHAT_COPY_TRANSCRIPT");
                  copyToClipboard(
                    formatTranscript(messages),
                    formatTranscriptHtml(messages)
                  );
                }}>
                <img src={CopyIcon} alt="" />
              </button>
              <button
                type="button"
                className="chat-icon-btn"
                title="Download bookmarks"
                aria-label="Download bookmarks"
                onClick={() => handleDownloadBookmarks(bookmarks)}>
                <img src={DownloadIcon} alt="" />
              </button>
              <ResetSessionButton />
            </Fragment>
          )}
          <ChatFilterPanel />
        </div>
      </div>

      <div className="chat-panel-body" ref={bodyRef}>
        {showThread ? (
          <MessageList
            messages={messages}
            status={status}
            renderMessageExtras={renderMessageExtras}
            scrollContainerRef={bodyRef}
          />
        ) : (
          <div className="chat-empty-state">
            Ask me anything about offerings, quals or business challenges.
          </div>
        )}
      </div>

      <GenAiSearchBar />
      <RatingCommentModal />
    </div>
  );
};

export default connect(
  ({ chat: { messages, status }, bookmarks }) => ({
    messages,
    status,
    bookmarks: {
      offeringTopicsCodes: bookmarks?.offeringTopicsCodes || [],
      qualificationsCodes: bookmarks?.qualificationsCodes || [],
    },
  }),
  dispatch => ({
    openRatingModal: payload => dispatch(OPEN_RATING_MODAL(payload)),
  })
)(ChatPanel);
